import { App as AntdApp, ConfigProvider, Spin } from 'antd';
import enUs from 'antd/lib/locale/en_US';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { RightPanelProvider } from './contexts/RightPanelContext';
import { SettingSaveProvider } from './contexts/SettingSaveContext';
import { FileTypeEnum } from './enums/FileTypeEnum';
import { SettingTypeEnum } from './enums/SettingTypeEnum';
import { usePermissions } from './hooks/usePermissions';
import AuthLayout from './layouts/AuthLayout';
import ExternalLayout from './layouts/ExternalLayout';
import MainLayout from './layouts/MainLayout';
import Login from './routes/Login';
import Office from './routes/Office';
import ProtectedFile from './routes/ProtectedFile';
import ProtectedFileToken from './routes/ProtectedFileToken';
import QuickShare from './routes/QuickShare';
import TrashFiles from './routes/TrashFiles';
import { slugify } from './utils/utils';

const Users = lazy(() => import('./routes/Users'));
const Groups = lazy(() => import('./routes/Groups'));
const SharedFiles = lazy(() => import('./routes/SharedFiles'));
const FavoritesFiles = lazy(() => import('./routes/FavoritesFiles'));
const HiddenFiles = lazy(() => import('./routes/HiddenFiles'));
const ReceivedFiles = lazy(() => import('./routes/ReceivedFiles'));
const PersonalFiles = lazy(() => import('./routes/PersonalFiles'));
const Settings = lazy(() => import('./routes/Settings'));
const Viewer = lazy(() => import('./routes/Viewer'));
const Store = lazy(() => import('./routes/Store'));
const PaymentSuccess = lazy(() => import('./routes/PaymentSuccess'));
const SalesDashboard = lazy(() => import('./routes/SalesDashboard'));
const SettingCompanyBranding = lazy(
  () => import('./routes/settings/SettingCompanyBranding')
);
const SettingAdminRegisteredMemberVisibility = lazy(
  () => import('./routes/settings/SettingAdminRegisteredMemberVisibility')
);
const SettingDownloadRestrictions = lazy(
  () => import('./routes/settings/SettingDownloadRestrictions')
);
const SettingForcedAntiCaptureMode = lazy(
  () => import('./routes/settings/SettingForcedAntiCaptureMode')
);
const SettingFileSizeLimit = lazy(
  () => import('./routes/settings/SettingFileSizeLimit')
);
const SettingCertifiedTimestamping = lazy(
  () => import('./routes/settings/SettingCertifiedTimestamping')
);
const SettingContributorStorageLimit = lazy(
  () => import('./routes/settings/SettingContributorStorageLimit')
);
const SettingDeviceAccessLimitation = lazy(
  () => import('./routes/settings/SettingDeviceAccessLimitation')
);
const SettingEditorFileManagement = lazy(
  () => import('./routes/settings/SettingEditorFileManagement')
);
const SettingExternalFileSharingRestriction = lazy(
  () => import('./routes/settings/SettingExternalFileSharingRestriction')
);
const SettingFileSalesPermission = lazy(
  () => import('./routes/settings/SettingFileSalesPermission')
);
const SettingFileTypeRestriction = lazy(
  () => import('./routes/settings/SettingFileTypeRestriction')
);
const SettingFolderCreationPermission = lazy(
  () => import('./routes/settings/SettingFolderCreationPermission')
);
const SettingGroupCreationControl = lazy(
  () => import('./routes/settings/SettingGroupCreationControl')
);
const SettingOnBehalfMode = lazy(
  () => import('./routes/settings/SettingOnBehalfMode')
);
const SettingOnlineEditors = lazy(
  () => import('./routes/settings/SettingOnlineEditors')
);
const SettingPublicAccessLink = lazy(
  () => import('./routes/settings/SettingPublicAccessLink')
);
const SettingRetentionPolicies = lazy(
  () => import('./routes/settings/SettingRetentionPolicies')
);
const SettingSessionControl = lazy(
  () => import('./routes/settings/SettingSessionControl')
);
const SettingSingleSignOn = lazy(
  () => import('./routes/settings/SettingSingleSignOn')
);
const SettingTagBasedSharingRestriction = lazy(
  () => import('./routes/settings/SettingTagBasedSharingRestriction')
);
const SettingUploadWithTag = lazy(
  () => import('./routes/settings/SettingUploadWithTag')
);
const SettingWatermarkProtection = lazy(
  () => import('./routes/settings/SettingWatermarkProtection')
);

function App() {
  const { brandingData } = usePermissions();
  return (
    <ConfigProvider
      locale={enUs}
      theme={{
        token: {
          colorPrimary: '#193057',
          colorInfo: '#193057',
          colorSuccess: '#00b048',
          colorError: '#f73b3b',
          borderRadius: 5,
          wireframe: true,
        },
        components: {
          Table: {
            lineHeight: 1,
            bodySortBg: 'rgb(255,255,255,0)',
            padding: 14,
            rowSelectedBg: 'rgba(25,48,87,0.1)',
            rowSelectedHoverBg: 'rgba(25,48,87,0.2)',
            motionDurationMid: '0s',
            motionDurationSlow: '0s',
            rowHoverBg: '#eeeffa',
          },
          Select: {
            optionSelectedColor: 'rgb(0,0,0)',
            optionSelectedBg: 'rgba(25,48,87,0.1)',
            activeOutlineColor: 'rgba(255,255,255,0)',
          },
          Input: {
            activeShadow: '0 0 0 0 rgba(255,255,255,0)',
            errorActiveShadow: '0 0 0 0 rgba(255,255,255,0)',
            warningActiveShadow: '0 0 0 0 rgba(255,255,255,0)',
          },
          Button: {
            colorPrimary: brandingData?.mainButtonsColorBackground || '#193057',
            defaultShadow: '0 1px 0 rgba(0,0,0,0.01)',
            primaryShadow: '0 1px 0 rgba(5,145,255,0.1)',
            dangerShadow: '0 1px 0 rgba(255,38,5,0.06)',
          },
          List: {
            descriptionFontSize: 10,
            titleMarginBottom: 2,
          },
          Modal: {
            motionDurationMid: '0s',
            motionDurationSlow: '0s',
          },
          Dropdown: {
            motionDurationMid: '0.1s',
            motionEaseInOutCirc: 'ease-in-out',
            motionEaseInQuint: 'ease-in',
            motionEaseOutCirc: 'ease-out',
            motionEaseOutQuint: 'ease-out',
          },
        },
      }}
    >
      <AntdApp className="h-full">
        <Routes>
          <Route
            path="/viewer/:type"
            element={
              <Suspense fallback={<Spin />}>
                <Viewer />
              </Suspense>
            }
          />
          <Route
            path="/word/:fileGuid"
            element={<Office type={FileTypeEnum.Word} />}
          />
          <Route
            path="/excel/:fileGuid"
            element={<Office type={FileTypeEnum.Excel} />}
          />
          <Route
            path="/powerpoint/:fileGuid"
            element={<Office type={FileTypeEnum.Powerpoint} />}
          />
          <Route
            path="/wopitest/:fileGuid"
            element={<Office type={FileTypeEnum.Wopitest} />}
          />
          {/*<Route path="/payment/:fileId" element={<Payment />} />*/}
          <Route path="/store/:fileId" element={<Store />} />
          <Route path="/payment/success/:fileId" element={<PaymentSuccess />} />
          <Route element={<AuthLayout />}>
            <Route path="login" element={<Login />} />
            <Route path="file/:fileId" element={<Login />} />
            <Route path="file/protected/:fileUid" element={<ProtectedFile />} />
            <Route
              path="file/protected/:fileUid/:token"
              element={<ProtectedFileToken />}
            />
          </Route>
          {/* <Route path="files" element={<FG_FileManager />} /> */}
          <Route element={<ExternalLayout />}>
            <Route path="quick-share" element={<QuickShare />} />
          </Route>
          <Route
            element={
              <RightPanelProvider>
                <MainLayout />
              </RightPanelProvider>
            }
          >
            <Route path="files">
              <Route path="personal">
                <Route index element={<PersonalFiles />} />
                <Route path="folder/:folderUid" element={<PersonalFiles />} />
              </Route>

              <Route path="received">
                <Route index element={<ReceivedFiles />} />
                <Route path="folder/:folderUid" element={<ReceivedFiles />} />
              </Route>

              <Route path="shared">
                <Route index element={<SharedFiles />} />
                <Route path="folder/:folderUid" element={<SharedFiles />} />
              </Route>

              <Route path="favorites">
                <Route index element={<FavoritesFiles />} />
                <Route path="folder/:folderUid" element={<FavoritesFiles />} />
              </Route>

              <Route path="hidden">
                <Route index element={<HiddenFiles />} />
                <Route path="folder/:folderUid" element={<HiddenFiles />} />
              </Route>
              <Route path="trash" element={<TrashFiles />} />
            </Route>

            <Route path="users" element={<Users />} />
            <Route path="groups" element={<Groups />} />
            <Route path="sales-dashboard" element={<SalesDashboard />} />
            <Route
              path="settings"
              element={
                <SettingSaveProvider>
                  <Outlet />
                </SettingSaveProvider>
              }
            >
              <Route index element={<Settings />} />
              <Route
                path={slugify(SettingTypeEnum.CompanyBranding)}
                element={<SettingCompanyBranding />}
              />
              <Route
                path={slugify(SettingTypeEnum.SingleSignOn)}
                element={<SettingSingleSignOn />}
              />
              <Route
                path={slugify(SettingTypeEnum.SessionControl)}
                element={<SettingSessionControl />}
              />
              <Route
                path={slugify(SettingTypeEnum.DeviceAccessLimitation)}
                element={<SettingDeviceAccessLimitation />}
              />
              <Route
                path={slugify(SettingTypeEnum.GroupCreationControl)}
                element={<SettingGroupCreationControl />}
              />
              <Route
                path={slugify(SettingTypeEnum.OnlineEditors)}
                element={<SettingOnlineEditors />}
              />
              <Route
                path={slugify(SettingTypeEnum.ContributorStorageLimit)}
                element={<SettingContributorStorageLimit />}
              />
              <Route
                path={slugify(SettingTypeEnum.CertifiedTimestamping)}
                element={<SettingCertifiedTimestamping />}
              />
              <Route
                path={slugify(SettingTypeEnum.AdminRegisteredMemberVisibility)}
                element={<SettingAdminRegisteredMemberVisibility />}
              />
              <Route
                path={slugify(SettingTypeEnum.ExternalFileSharingRestriction)}
                element={<SettingExternalFileSharingRestriction />}
              />
              <Route
                path={slugify(SettingTypeEnum.RetentionPolicies)}
                element={<SettingRetentionPolicies />}
              />
              <Route
                path={slugify(SettingTypeEnum.FolderCreationPermission)}
                element={<SettingFolderCreationPermission />}
              />
              <Route
                path={slugify(SettingTypeEnum.EditorFileManagement)}
                element={<SettingEditorFileManagement />}
              />
              <Route
                path={slugify(SettingTypeEnum.FileTypeRestriction)}
                element={<SettingFileTypeRestriction />}
              />
              <Route
                path={slugify(SettingTypeEnum.PublicAccessLink)}
                element={<SettingPublicAccessLink />}
              />
              <Route
                path={slugify(SettingTypeEnum.DownloadRestrictions)}
                element={<SettingDownloadRestrictions />}
              />
              <Route
                path={slugify(SettingTypeEnum.ForcedAntiCaptureMode)}
                element={<SettingForcedAntiCaptureMode />}
              />
              <Route
                path={slugify(SettingTypeEnum.UploadWithTag)}
                element={<SettingUploadWithTag />}
              />
              <Route
                path={slugify(SettingTypeEnum.TagBasedSharingRestriction)}
                element={<SettingTagBasedSharingRestriction />}
              />
              <Route
                path={slugify(SettingTypeEnum.FileSalesPermission)}
                element={<SettingFileSalesPermission />}
              />
              <Route
                path={slugify(SettingTypeEnum.WatermarkProtection)}
                element={<SettingWatermarkProtection />}
              />
              <Route
                path={slugify(SettingTypeEnum.FileSizeLimit)}
                element={<SettingFileSizeLimit />}
              />
              <Route
                path={slugify(SettingTypeEnum.OnBehalfMode)}
                element={<SettingOnBehalfMode />}
              />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
